import { Info, MoreVertical, Edit, Copy, File as FileExport, Trash2, Import, Plus, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { TableTag } from '@/components/autopilot/table-tag';
import { ProfileTag } from '@/components/autopilot/profile-status';
import { useProfiles } from '@/api/hooks/useProfile';
import { useEffect, useState } from 'react';
import { App, message, Spin } from 'antd';
import { ProfileModal } from '@/components/autopilot/profile-modal';
import { SUCCESS_MESSAGE } from '@/utils/constants';
import { deleteProfile, duplicateProfile } from '@/api/profile';
import { useNavigate } from 'react-router-dom';

interface Profile {
  id: number;
  name: string;
  status: string;
  automations: string[];
  schedule: string;
}

export function Profiles() {
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [isVisibleProfileModal, setIsVisibleProfileModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const { message } = App.useApp();
  const { data: profilesApiData, isLoading: profilesLoading, refetch } = useProfiles();
  const navigate = useNavigate();
    
  useEffect(() => {
    refetch()
  }, [navigate])

  useEffect(() => {
    if (!profilesApiData) return;
    setProfiles(profilesApiData?.map(profile => ({
      id: profile.id,
      name: profile.name,
      status: profile.status,
      automations: profile.automation_profiles.map((automation: { automation: { name: string; }; }) => automation.automation.name),
      automation_profiles: profile.automation_profiles,
      schedule: profile.schedule,
    })))
  }, [profilesApiData]);

  const handleDuplicate = async (id) => {
    try {
      await duplicateProfile(id);
      refetch()
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteProfile(id);
      refetch()
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  }

  if (profilesLoading || !profiles) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center mb-2">
          <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-9 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
        
        <div className="grid gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-light-grey p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
                  <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                </div>
                <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                  <div className="flex gap-1">
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      
      <div className="flex justify-between items-center mb-2">
        <h2 className="heading-6 text-dark-blue">Profiles</h2>
        <div className="flex gap-2">
          <Button
            variant="default"
            size="md"
            className="bg-primary text-white"
            onClick={() => {
              setIsVisibleProfileModal(true)
              setSelectedProfile(null)
            }}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            <p className="button-s">Create</p>
          </Button>
        </div>
      </div>

      <div className="px-1">
        <div className="border border-white-blue rounded-[8px] overflow-x-auto">
        <table className="w-full">
          <thead className="bg-bg">
            <tr>
              <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Profile Name</th>
              <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Status</th>
              <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Automations Used</th>
              <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Schedule</th>
              <th className="py-2 px-4 bg-light-grey"></th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((profile, idx) => (
              <tr
                key={`${profile.name}_${idx}`} 
                className={cn(
                  "border-t border-light-grey bg-white hover:bg-bg/50 hover:cursor-pointer",
                  idx % 2 === 1 && "bg-bg",
                )}
                onClick={() => {
                  setSelectedProfile(profile)
                  setIsVisibleProfileModal(true)
                  setIsEdit(false)
                }}
              >
                <td className="heading-7 px-4 text-dark-blue">{profile.name}</td>
                <td className="py-2 px-4">
                  <ProfileTag text={profile.status} className={profile.status === "in progress" ? "border-light-green bg-bleached-green text-mid-green" : ""} />
                </td>
                <td className="px-4">
                  <div className="flex items-center gap-1">
                  {profile.automations.map(v => <TableTag text={v}/>)}
                  </div>
                </td>
                <td className="paragraph-12 text-black px-4">{profile.schedule}</td>
                <td className="py-2 px-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => {
                        setSelectedProfile(profile)
                        setIsVisibleProfileModal(true)
                        setIsEdit(true)
                      }}>
                        <Edit className="h-4 w-4 mr-2" />
                        <p className="paragraph-11 text-[#5E5E5E]">Edit</p>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(profile.id)}>
                        <Copy className="h-4 w-4 mr-2" />
                        <p className="paragraph-11 text-[#5E5E5E]">Duplicate</p>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(profile.id)}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        <p className="paragraph-11">Delete</p>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      {isVisibleProfileModal && (
        <ProfileModal
          isOpen={true}
          onClose={() => setIsVisibleProfileModal(false)}
          message={message}
          edit={isEdit}
          data={selectedProfile}
          refetch={refetch}
        />
      )}
    </div>
  );
}